import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      import("@/lib/push").then((m) => m.initPush()).catch(() => {});
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me when returning from Emergent OAuth; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const applyAuth = (data) => {
    if (data?.token) localStorage.setItem("cr_token", data.token);
    setUser(data.user);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.twofa_required) return { twofa_required: true, challenge_token: data.challenge_token };
    applyAuth(data);
    return data.user;
  };

  const verify2fa = async (challenge_token, code) => {
    const { data } = await api.post("/auth/2fa/login", { challenge_token, code });
    applyAuth(data);
    return data.user;
  };

  const register = async (name, email, password, opts = {}) => {
    const { data } = await api.post("/auth/register", { name, email, password, ...opts });
    applyAuth(data);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("cr_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2fa, register, logout, setUser, applyAuth, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
